import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Property, SearchFilters } from '../types';
import { useUser } from './UserContext';
import { supabase } from '../integrations/supabase/client';
import { generatePropertyCode } from '../utils/propertyCodeGenerator';
import { translateAmenities, translateAmenitiesToEnglish } from '../utils/translations';

// Interface do contexto com função única de salvamento
interface PropertyContextType {
  properties: Property[];
  searchFilters: SearchFilters;
  setSearchFilters: (filters: SearchFilters) => void;
  filteredProperties: Property[];
  getPropertyById(id: string): Property | undefined;
  getRecommendedProperties(): Property[];
  getPropertiesByCategory(category: string): Property[];
  saveProperty(property: Property): Promise<Property>; // Função única de salvamento
  addProperty(property: Property): Promise<Property>; // Wrapper para compatibilidade
  updateProperty(property: Property): Promise<Property>; // Wrapper para compatibilidade
  deleteProperty(id: string): void;
  refreshProperties(): Promise<void>;
}

const PropertyContext = createContext<PropertyContextType | undefined>(undefined);

const initialFilters: SearchFilters = {
  transactionType: '',
  location: '',
  priceMin: 0,
  priceMax: 0,
  bedrooms: 0,
  suites: 0,
  parkingSpaces: 0,
  type: '',
  category: ''
};

interface PropertyProviderProps {
  children: ReactNode;
}

export const PropertyProvider: React.FC<PropertyProviderProps> = ({ children }) => {
  const [allProperties, setAllProperties] = useState<Property[]>([]);
  const [searchFilters, setSearchFilters] = useState<SearchFilters>(initialFilters);
  const [isInitialized, setIsInitialized] = useState(false);
  const { currentUser } = useUser();

  console.log('🏠 PropertyProvider: Iniciando provider...');

  // ============= TRADUÇÕES (APENAS UI) =============
  const propertyTypeToEng = (type: string): string => {
    const map: { [key: string]: string } = {
      'Apartamento': 'apartment',
      'Casa': 'house',
      'Kitnet': 'studio',
      'Sobrado': 'townhouse',
      'Cobertura': 'penthouse',
      'Loft': 'loft',
      'Flat': 'flat',
      'Terreno': 'land',
      'Chácara': 'farm',
      'Sítio': 'ranch',
      'Pousada': 'inn',
      'Hotel': 'hotel',
      'Ponto Comercial': 'commercial',
      'Sala Comercial': 'office',
      'Galpão': 'warehouse',
      'Prédio': 'building'
    };
    return map[type] || type;
  };

  const propertyTypeToPort = (type: string): string => {
    const map: { [key: string]: string } = {
      'apartment': 'Apartamento',
      'house': 'Casa',
      'studio': 'Kitnet',
      'townhouse': 'Sobrado',
      'penthouse': 'Cobertura',
      'loft': 'Loft',
      'flat': 'Flat',
      'land': 'Terreno',
      'farm': 'Chácara',
      'ranch': 'Sítio',
      'inn': 'Pousada',
      'hotel': 'Hotel',
      'commercial': 'Ponto Comercial',
      'office': 'Sala Comercial',
      'warehouse': 'Galpão',
      'building': 'Prédio'
    };
    return map[type] || type;
  };

  // Mantemos apenas a conversão para exibição (PT) abaixo; a escrita para o DB usa normalização direta no saveProperty
  const transactionTypeToPort = (type: string): string => {
    const t = (type || '').toString().toLowerCase();
    const map: { [key: string]: string } = {
      'sale': 'Venda',
      'rent': 'Aluguel',
      'both': 'Venda/Aluguel',
      'for sale': 'Venda',
      'for rent': 'Aluguel',
      'sale/rent': 'Venda/Aluguel'
    };
    const result = map[t] || (type as string);
    console.log('🔄 transactionTypeToPort:', { input: type, normalized: t, output: result });
    return result;
  };

  // ============= CONVERSÃO DB -> PROPERTY =============
  const convertDbToProperty = async (dbRow: any): Promise<Property> => {
    console.log('🔄 Convertendo DB row para Property:', {
      id: dbRow.id,
      listing_id: dbRow.listing_id,
      list_price: dbRow.list_price,
      rental_price: dbRow.rental_price,
      transaction_type: dbRow.transaction_type,
      title: dbRow.title?.substring(0, 30) + '...'
    });
    
    // Fetch owner contact info from secure table (only if authenticated)
    let ownerData = {
      name: dbRow.owner_name || '***',
      phone: dbRow.owner_phone || '***',
      email: dbRow.owner_email || '***'
    };

    try {
      const { data: session } = await supabase.auth.getSession();
      if (session?.session) {
        // User is authenticated, try to fetch owner contacts
        const { data: ownerContact, error } = await supabase
          .from('property_owner_contacts')
          .select('owner_name, owner_phone, owner_email')
          .eq('property_id', dbRow.id)
          .single();

        if (!error && ownerContact) {
          ownerData = {
            name: ownerContact.owner_name,
            phone: ownerContact.owner_phone,
            email: ownerContact.owner_email || ''
          };
        }
      }
    } catch (error) {
      // Silently fallback to masked data for unauthenticated users
      console.log('ℹ️ Owner contact data not available (user not authenticated or no permission)');
    }
    
    const converted = {
      id: dbRow.id,
      listingId: dbRow.listing_id,
      propertyCode: dbRow.listing_id,
      title: dbRow.title,
      description: dbRow.description || '',
      propertyType: propertyTypeToPort(dbRow.property_type),
      transactionType: transactionTypeToPort(dbRow.transaction_type) as "Venda" | "Aluguel" | "Venda/Aluguel",
      publicationType: 'STANDARD' as const,
      listDate: dbRow.created_at,
      detailViewUrl: '',
      listPrice: dbRow.list_price || 0,
      rentalPrice: dbRow.rental_price || 0,
      propertyAdministrationFee: dbRow.condominium_fee || 0,
      yearlyTax: dbRow.yearly_iptu || 0,
      bedrooms: dbRow.bedrooms || 0,
      suites: dbRow.suites || 0,
      bathrooms: dbRow.bathrooms || 0,
      garage: {
        quantity: dbRow.garage_quantity || 0,
        type: 'Covered'
      },
      livingArea: dbRow.area_built || 0,
      totalArea: dbRow.area_total || dbRow.area_built || 0,
      features: (() => {
        const features = Array.isArray(dbRow.amenities) ? dbRow.amenities : [];
        console.log('🎯 PropertyContext - amenities do banco (RAW):', dbRow.amenities);
        // Traduzir de inglês para português para uso na UI
        const translatedFeatures = translateAmenities(features);
        console.log('🎯 PropertyContext - features traduzidas para PT:', translatedFeatures);
        console.log('🎯 PropertyContext - features length:', translatedFeatures.length);
        return translatedFeatures;
      })(),
      location: {
        country: 'Brasil',
        state: dbRow.state || 'SP',
        city: dbRow.city || '',
        neighborhood: dbRow.neighborhood || '',
        address: dbRow.address || '',
        streetNumber: dbRow.street_number || '',
        complement: dbRow.complement || '',
        postalCode: dbRow.zip_code || '',
        latitude: dbRow.latitude || 0,
        longitude: dbRow.longitude || 0
      },
      tower: dbRow.tower || '',
      media: {
        images: Array.isArray(dbRow.images) ? dbRow.images : [],
        youtubeVideoUrl: dbRow.video_url || '',
        virtualTourUrl: dbRow.virtual_tour_url || ''
      },
      contact: {
        brokerName: 'FOX VENDAS',
        brokerEmail: 'victor@foxvendas.com.br',
        brokerPhone: '19 32178399',
        brokerPhoto: '',
        companySite: 'https://foxvendas.com.br',
        companyLogo: 'https://foxvendas.com.br/logo.png'
      },
      owner: ownerData,
      keysLocation: dbRow.keys_location || 'Na imobiliária',
      condominiumId: dbRow.condominium_id || undefined,
      userId: dbRow.responsible_broker_id || dbRow.user_id || '',
      category: dbRow.category || 'Médio',
      isActive: dbRow.is_active || false,
      createdAt: new Date(dbRow.created_at),
      lastUpdateDate: dbRow.updated_at, // Campo chave para versionamento
      sendLeadsToQueue: dbRow.send_leads_to_queue || false,
      olx: dbRow.olx || { sendToOlx: false, olxType: 'STANDARD' }
    };
    
    console.log('✅ Property convertida:', {
      id: converted.id,
      listingId: converted.listingId,
      title: converted.title?.substring(0, 30) + '...',
      transactionType: converted.transactionType,
      listPrice: converted.listPrice,
      rentalPrice: converted.rentalPrice,
      propertyAdministrationFee: converted.propertyAdministrationFee,
      yearlyTax: converted.yearlyTax
    });
    
    return converted;
  };
  const fetchProperties = async (retryCount = 0) => {
    try {
      console.log('🔄 PropertyContext - Buscando propriedades do banco...');
      
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ PropertyContext - Erro ao buscar propriedades:', error);
        if (retryCount < 2) {
          console.log(`🔄 PropertyContext - Tentativa ${retryCount + 1}/3 em 2s...`);
          await new Promise(resolve => setTimeout(resolve, 2000));
          return fetchProperties(retryCount + 1);
        }
        throw error;
      }

      console.log('✅ PropertyContext - Propriedades carregadas:', data?.length || 0);
      
      const properties = await Promise.all((data || []).map(convertDbToProperty));
      setAllProperties(properties);
      
    } catch (error) {
      console.error('❌ PropertyContext - Erro fatal ao buscar propriedades:', error);
      throw error;
    }
  };

  const refreshProperties = async () => {
    console.log('🔄 PropertyContext - Refresh manual solicitado');
    await fetchProperties();
  };

  // ============= CARREGAR PROPRIEDADES INICIAL =============
  useEffect(() => {
    console.log('🚀 PropertyContext - Carregamento inicial');
    fetchProperties().finally(() => {
      setIsInitialized(true);
      console.log('✅ PropertyContext - Inicialização completa');
    });
  }, []);

  // ============= REALTIME COM CONTROLE DE VERSÃO =============
  useEffect(() => {
    let debounceTimer: NodeJS.Timeout;
    
    const channel = supabase
      .channel('schema-db-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'properties'
        },
        (payload) => {
          console.log('🔄 Realtime: Mudança detectada:', payload.eventType, payload);
          
          // Debounce para evitar múltiplas atualizações
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            handleRealtimeEvent(payload);
          }, 300);
        }
      )
      .subscribe();

    return () => {
      clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, []);

  // ============= HANDLER REALTIME COM VERSIONAMENTO =============
  const handleRealtimeEvent = async (payload: any) => {
    console.log('📡 handleRealtimeEvent:', payload.eventType, payload);
    
    if (payload.eventType === 'INSERT') {
      console.log('➕ Realtime INSERT - refreshing properties');
      await fetchProperties();
      
    } else if (payload.eventType === 'UPDATE') {
      const newRecord = payload.new;
      const incomingUpdatedAt = new Date(newRecord.updated_at);
      
      console.log('🔄 Realtime UPDATE - verificando versão:', {
        id: newRecord.id,
        listing_id: newRecord.listing_id,
        incoming_updated_at: incomingUpdatedAt
      });
      
      // Convert the new record asynchronously
      const updatedProperty = await convertDbToProperty(newRecord);
      
      setAllProperties(prev => 
        prev.map(localProperty => {
          if (localProperty.id === newRecord.id || localProperty.listingId === newRecord.listing_id) {
            const localUpdatedAt = localProperty.lastUpdateDate ? new Date(localProperty.lastUpdateDate) : new Date(0);
            
            // Só mesclar se a versão remota é mais recente
            if (incomingUpdatedAt > localUpdatedAt) {
              console.log('✅ Realtime: Mesclando versão mais recente do servidor');
              return updatedProperty;
            } else {
              console.log('⏭️ Realtime: Ignorando versão mais antiga (local é mais recente)');
              return localProperty;
            }
          }
          return localProperty;
        })
      );
      
    } else if (payload.eventType === 'DELETE') {
      console.log('🗑️ Realtime DELETE');
      const oldRecord = payload.old;
      setAllProperties(prev => 
        prev.filter(p => p.id !== oldRecord.id && p.listingId !== oldRecord.listing_id)
      );
    }
  };

  // ============= FUNÇÃO ÚNICA DE SALVAMENTO (SINGLE SOURCE OF TRUTH) =============
  const saveProperty = async (property: Property): Promise<Property> => {
    const isUpdate = !!property.id;
    console.log(`${isUpdate ? '🔄 UPDATE' : '➕ CREATE'} PropertyContext - Iniciando saveProperty para:`, property.title);
    console.log('📊 PropertyContext - Dados recebidos:', property);
    console.log('🖼️ PropertyContext - Processando imagens:', property.media?.images);
    
    try {
      // Upload das imagens para o Supabase Storage
      const uploadedImages: Array<{id: string, url: string, name: string, primary: boolean}> = [];
      
      if (property.media?.images?.length > 0) {
        for (let i = 0; i < property.media.images.length; i++) {
          const image = property.media.images[i];
          
          // Se a imagem tem um arquivo (File object), fazer upload
          if (image.file) {
            console.log(`📤 Fazendo upload da nova imagem ${i + 1}:`, image.name);
            
            const fileExt = image.file.name.split('.').pop();
            const fileName = `${property.listingId || Date.now()}/${Date.now()}_${i}.${fileExt}`;
            
            const { error: uploadError } = await supabase.storage
              .from('property-images')
              .upload(fileName, image.file);
            
            if (uploadError) {
              console.error('❌ Erro no upload da imagem:', uploadError);
              throw uploadError;
            }
            
            // Obter URL pública da imagem
            const { data: urlData } = supabase.storage
              .from('property-images')
              .getPublicUrl(fileName);
            
            uploadedImages.push({
              id: image.id || '',
              url: urlData.publicUrl,
              name: image.name || '',
              primary: image.primary || false
            });
            
            console.log(`✅ Upload concluído para imagem ${i + 1}:`, urlData.publicUrl);
          } else if (image.url) {
            // Se já tem URL (imagem existente), manter a imagem
            uploadedImages.push({
              id: image.id || '',
              url: image.url,
              name: image.name || '',
              primary: image.primary || false
            });
          }
        }
      }

      // Preparar dados para DB (formato canônico)
      const dbData = {
        title: property.title,
        description: property.description,
        property_type: propertyTypeToEng(property.propertyType), // Formato canônico
        transaction_type: (() => {
          const t = (property.transactionType || '').toString().toLowerCase().trim();
          // Map incoming to DB-allowed values
          const mapToDb: { [key: string]: string } = {
            'venda': 'For Sale',
            'aluguel': 'For Rent',
            'venda/aluguel': 'Sale/Rent',
            'sale': 'For Sale',
            'rent': 'For Rent',
            'both': 'Sale/Rent',
            'for sale': 'For Sale',
            'for rent': 'For Rent',
            'sale/rent': 'Sale/Rent'
          };
          let dbVal = mapToDb[t];
          if (!dbVal) {
            // Infer by values if missing or unknown
            const inferred = (property.listPrice && property.rentalPrice)
              ? 'Sale/Rent'
              : (property.rentalPrice ? 'For Rent' : 'For Sale');
            dbVal = inferred;
          }
          console.log('🔄 Normalizando transaction_type (DB):', {
            original: property.transactionType,
            normalized: t,
            dbVal
          });
          return dbVal;
        })(),
        list_price: property.listPrice || 0,
        rental_price: property.rentalPrice || 0,
        condominium_fee: property.propertyAdministrationFee || 0,
        yearly_iptu: property.yearlyTax || 0,
        bedrooms: property.bedrooms,
        suites: property.suites,
        bathrooms: property.bathrooms,
        garage_quantity: property.garage.quantity,
        area_built: property.livingArea,
        area_total: property.totalArea || property.livingArea,
        address: property.location.address,
        street_number: property.location.streetNumber,
        complement: property.location.complement || null,
        tower: property.tower,
        neighborhood: property.location.neighborhood,
        city: property.location.city,
        state: property.location.state,
        zip_code: property.location.postalCode,
        latitude: property.location.latitude,
        longitude: property.location.longitude,
        amenities: (() => {
          const en = translateAmenitiesToEnglish(property.features || []);
          console.log('🎯 PropertyContext - amenities enviadas (EN):', en);
          return en as any;
        })(),
        images: uploadedImages as any,
        video_url: property.media.youtubeVideoUrl,
        virtual_tour_url: property.media.virtualTourUrl,
        keys_location: property.keysLocation,
        condominium_id: property.condominiumId || null,
        // user_id: mantido no banco; não sobrescrever no UPDATE
        responsible_broker_id: property.userId || currentUser?.id || undefined, // Usar currentUser como fallback
        category: property.category,
        send_leads_to_queue: property.sendLeadsToQueue || false,
        olx: property.olx as any,
        // Required fields from properties table
        owner_name: property.owner.name,
        owner_phone: property.owner.phone,
        owner_email: property.owner.email || null
      };

      // Save owner contact info to secure table
      const ownerContactData = {
        property_id: property.id, // Will be set after insert/update
        owner_name: property.owner.name,
        owner_phone: property.owner.phone,
        owner_email: property.owner.email || null
      };

      let result: any;

      if (isUpdate) {
        // ============= UPDATE ATÔMICO =============
        console.log('📡 PropertyContext - UPDATE: Enviando dados para Supabase:', dbData);
        console.log('🎯 PropertyContext - UPDATE: amenities específicas sendo enviadas:', dbData.amenities);
        
        const { data, error } = await supabase
          .from('properties')
          .update(dbData)
          .eq('id', property.id)
          .select()
          .single(); // Retorna o registro atualizado com updated_at

        if (error) {
          console.error('❌ PropertyContext - Erro no UPDATE:', error);
          throw error;
        }

        if (!data) {
          throw new Error('Imóvel não encontrado para atualização.');
        }

        result = data;
        console.log('✅ PropertyContext - UPDATE concluído:', result);
        console.log('🎯 PropertyContext - UPDATE: amenities retornadas do banco:', result.amenities);

        // Update owner contact info in secure table
        if (property.owner.name && property.owner.phone) {
          const { error: ownerError } = await supabase
            .from('property_owner_contacts')
            .upsert({
              ...ownerContactData,
              property_id: result.id
            }, {
              onConflict: 'property_id'
            });

          if (ownerError) {
            console.error('⚠️ Erro ao atualizar contatos do proprietário:', ownerError);
            // Don't throw - property was saved successfully
          }
        }
        
        // Atualizar estado local com dados do banco (incluindo updated_at)
        setAllProperties(prev => 
          prev.map(p => {
            if (p.id === property.id) {
              return {
                ...property,
                id: result.id,
                listingId: result.listing_id,
                propertyCode: result.listing_id,
                lastUpdateDate: result.updated_at,
                media: {
                  ...property.media,
                  images: uploadedImages
                }
              };
            }
            return p;
          })
        );
      } else {
        // ============= INSERT =============
        const propertyCode = property.propertyCode || generatePropertyCode(property.propertyType);
        console.log('💾 PropertyContext - INSERT: Criando propriedade com código:', propertyCode);

        const insertData: any = {
          ...dbData,
          listing_id: propertyCode,
          featured: false,
          is_active: true,
          user_id: currentUser?.id || property.userId // usar usuário autenticado ou captador do imóvel
        };

        const { data, error } = await supabase
          .from('properties')
          .insert(insertData)
          .select()
          .single(); // Retorna o registro criado com created_at e updated_at

        if (error) {
          console.error('❌ PropertyContext - Erro no INSERT:', error);
          throw error;
        }

        result = data;
        console.log('✅ PropertyContext - INSERT concluído:', result);

        // Insert owner contact info in secure table
        if (property.owner.name && property.owner.phone) {
          const { error: ownerError } = await supabase
            .from('property_owner_contacts')
            .insert({
              ...ownerContactData,
              property_id: result.id
            });

          if (ownerError) {
            console.error('⚠️ Erro ao inserir contatos do proprietário:', ownerError);
            // Don't throw - property was saved successfully
          }
        }

        // Atualizar estado local
        const newProperty = {
          ...property,
          id: result.id,
          listingId: propertyCode,
          propertyCode: propertyCode,
          createdAt: new Date(result.created_at),
          lastUpdateDate: result.updated_at,
          media: {
            ...property.media,
            images: uploadedImages
          }
        };
        
        setAllProperties(prev => [...prev, newProperty]);
      }

      // Retornar o property atualizado com dados do banco
      return {
        ...property,
        id: result.id,
        listingId: result.listing_id,
        propertyCode: result.listing_id,
        lastUpdateDate: result.updated_at,
        media: {
          ...property.media,
          images: uploadedImages
        }
      };

    } catch (error) {
      console.error(`❌ PropertyContext - Erro no ${isUpdate ? 'UPDATE' : 'INSERT'}:`, error);
      throw error;
    }
  };

  // ============= WRAPPERS PARA COMPATIBILIDADE =============
  const addProperty = async (property: Property) => {
    console.log('➕ addProperty: delegando para saveProperty (CREATE)');
    return await saveProperty(property);
  };

  const updateProperty = async (property: Property) => {
    console.log('🔄 updateProperty: delegando para saveProperty (UPDATE)');
    return await saveProperty(property);
  };

  // ============= DELETE PROPERTY =============
  const deleteProperty = (id: string) => {
    console.log('🗑️ PropertyContext - Removendo propriedade localmente:', id);
    setAllProperties(prev => prev.filter(property => property.listingId !== id));
  };

  // ============= FILTERED PROPERTIES =============
  const filteredProperties = allProperties.filter(property => {
    // CRÍTICO: Apenas imóveis ativos devem aparecer na busca pública
    if (!property.isActive) {
      return false;
    }
    
    // Filtro por tipo de transação (Venda/Aluguel)
    if (searchFilters.transactionType) {
      if (property.transactionType === 'Venda/Aluguel') {
        // Venda/Aluguel aparece tanto em busca de Venda quanto Aluguel
      } else if (property.transactionType !== searchFilters.transactionType) {
        return false;
      }
    }
    
    // Filtro por localização (cidade, bairro, endereço, código do imóvel, condomínio)
    if (searchFilters.location) {
      const search = searchFilters.location.toLowerCase();
      const matchesLocation = 
        property.location.city.toLowerCase().includes(search) ||
        property.location.neighborhood.toLowerCase().includes(search) ||
        property.location.address.toLowerCase().includes(search) ||
        property.listingId.toLowerCase().includes(search) ||
        property.propertyCode?.toLowerCase().includes(search) ||
        property.title.toLowerCase().includes(search);
      if (!matchesLocation) return false;
    }
    
    // Filtro por tipo de imóvel - ambos estão em inglês (ex: "Residential / Apartment")
    if (searchFilters.type) {
      if (property.propertyType !== searchFilters.type) {
        return false;
      }
    }
    
    // Filtro por preço - usar preço relevante baseado no tipo de transação
    const relevantPrice = (searchFilters.transactionType === 'Aluguel' || property.transactionType === 'Aluguel')
      ? (property.rentalPrice || 0)
      : (property.listPrice || 0);
    
    if (searchFilters.priceMin && searchFilters.priceMin > 0 && relevantPrice < searchFilters.priceMin) {
      return false;
    }
    if (searchFilters.priceMax && searchFilters.priceMax > 0 && relevantPrice > searchFilters.priceMax) {
      return false;
    }
    
    // Filtro por quartos
    if (searchFilters.bedrooms && searchFilters.bedrooms > 0 && property.bedrooms < searchFilters.bedrooms) {
      return false;
    }
    
    // Filtro por suítes
    if (searchFilters.suites && searchFilters.suites > 0 && property.suites < searchFilters.suites) {
      return false;
    }
    
    // Filtro por vagas
    if (searchFilters.parkingSpaces && searchFilters.parkingSpaces > 0 && property.garage.quantity < searchFilters.parkingSpaces) {
      return false;
    }
    
    return true;
  });

  // ============= GETTERS =============
  const getPropertyById = (id: string): Property | undefined => {
    return allProperties.find(property => property.listingId === id || property.id === id);
  };

  const getRecommendedProperties = (): Property[] => {
    return allProperties.filter(property => property.isActive && property.category === 'Alto Padrão').slice(0, 6);
  };

  const getPropertiesByCategory = (category: string): Property[] => {
    return allProperties.filter(property => property.isActive && property.category === category);
  };

  // ============= PROVIDER VALUE =============
  const value: PropertyContextType = {
    properties: allProperties,
    searchFilters,
    setSearchFilters,
    refreshProperties,
    filteredProperties,
    getPropertyById,
    getRecommendedProperties,
    getPropertiesByCategory,
    saveProperty, // Função única de salvamento
    addProperty, // Wrapper para compatibilidade
    updateProperty, // Wrapper para compatibilidade
    deleteProperty
  };

  // Renderizar um placeholder enquanto inicializa
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando imóveis...</p>
        </div>
      </div>
    );
  }

  return (
    <PropertyContext.Provider value={value}>
      {children}
    </PropertyContext.Provider>
  );
};

export const useProperty = (): PropertyContextType => {
  const context = useContext(PropertyContext);
  if (context === undefined) {
    console.warn('useProperty chamado fora de um PropertyProvider. Usando fallback seguro.');
    return {
      properties: [],
      searchFilters: initialFilters,
      setSearchFilters: () => {},
      filteredProperties: [],
      getPropertyById: () => undefined,
      getRecommendedProperties: () => [],
      getPropertiesByCategory: () => [],
      saveProperty: async (p: Property) => p,
      addProperty: async (p: Property) => p,
      updateProperty: async (p: Property) => p,
      deleteProperty: () => {},
      refreshProperties: async () => {}
    } as PropertyContextType;
  }
  return context;
};